import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import {
  DealSearchResult,
  EmailMessage,
  EmailThread,
} from 'models/emailIntegration'
import { ContactCompany } from 'models/companies'
import { EmailDetailActionBar } from './EmailDetailActionBar'
import { ThreadMessage } from './ThreadMessage'
import { EmailReplyBar } from 'components/Email'
import { ComposePanel, ComposeMode, SendPayload } from './ComposePanel'
import { handleEmailError } from './handleEmailError'
import { CATEGORY_BADGE_CLASS, DealBadge } from './DealBadge'
import { DealSearchSubContent } from './DealSearchSubContent'
import { UpdateSalesStageModal } from './UpdateSalesStageModal'

const ReminderModal = lazy(() => import('components/Reminders/ReminderModal'))
const CreateDealModal = lazy(
  () => import('components/Deals/CreateDealModal/CreateDealModal')
)

/** Skeleton for loading state. */
function ThreadSkeleton() {
  return (
    <div className="flex flex-col gap-6 p-6">
      {[0, 1].map((i) => (
        <div key={i} className="flex gap-3">
          <div className="w-10 h-10 rounded-full bg-surface-disabled animate-pulse flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <div className="h-4 w-32 bg-surface-disabled rounded animate-pulse" />
              <div className="h-4 w-40 bg-surface-disabled rounded animate-pulse" />
              <div className="ml-auto h-4 w-28 bg-surface-disabled rounded animate-pulse" />
            </div>
            <div className="h-3 w-48 bg-surface-disabled rounded animate-pulse" />
            <div className="mt-4 space-y-2">
              <div className="h-3 w-full bg-surface-disabled rounded animate-pulse" />
              <div className="h-3 w-11/12 bg-surface-disabled rounded animate-pulse" />
              <div className="h-3 w-4/5 bg-surface-disabled rounded animate-pulse" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

interface EmailDetailViewProps {
  thread: EmailThread
  threadIndex: number
  totalThreads: number
  pageSize: number
  pageStart: number
  contactCompanyId: number
  contactCompany?: ContactCompany
  companyName?: string
  initialComposeMode?: ComposeMode
  onBack: () => void
  onNavigateThread: (delta: number) => void
}

/** Thread-based email detail view with action bar, messages, and reply bar. */
export function EmailDetailView({
  thread,
  threadIndex,
  totalThreads,
  pageSize,
  pageStart,
  contactCompanyId,
  contactCompany,
  companyName,
  initialComposeMode,
  onBack,
  onNavigateThread,
}: EmailDetailViewProps) {
  const api = useApiService()
  const queryClient = useQueryClient()

  const { data: messages, isLoading } = useQuery({
    queryKey: ['thread-messages', thread.id],
    queryFn: ({ signal }) =>
      api.emailIntegration.getThreadMessages(thread.id, signal),
  })

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [composeMode, setComposeMode] = useState<ComposeMode | null>(null)
  const [replyToMessage, setReplyToMessage] = useState<
    EmailMessage | undefined
  >()
  const [showUpdateStage, setShowUpdateStage] = useState(false)
  const [showReminderModal, setShowReminderModal] = useState(false)
  const [showCreateDeal, setShowCreateDeal] = useState(false)
  const [isAssigningNewDeal, setIsAssigningNewDeal] = useState(false)
  const createDealOpenedAt = useRef<string | null>(null)

  // Local deal state for instant UI updates after assign/remove
  const [localDeal, setLocalDeal] = useState({
    id: thread.deal_id,
    identifier: thread.deal_identifier,
    name: thread.deal_name,
  })

  // Sync local deal state when thread prop changes (e.g. navigating threads)
  useEffect(() => {
    setLocalDeal({
      id: thread.deal_id,
      identifier: thread.deal_identifier,
      name: thread.deal_name,
    })
  }, [thread.id, thread.deal_id, thread.deal_identifier, thread.deal_name])

  // Local hidden state for instant UI updates after hide/unhide
  const [localIsHidden, setLocalIsHidden] = useState(thread.is_hidden)

  // Sync local hidden state when thread prop changes (e.g. navigating threads)
  useEffect(() => {
    setLocalIsHidden(thread.is_hidden)
  }, [thread.id, thread.is_hidden])

  const invalidateDealQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['email-threads'] })
    queryClient.invalidateQueries({
      queryKey: ['thread-messages', thread.id],
    })
  }, [queryClient, thread.id])

  const assignMutation = useMutation({
    mutationFn: (deal: { id: number; identifier: string; deal_name: string }) =>
      api.emailIntegration.assignThreadsToDeal({
        thread_ids: [thread.id],
        deal_id: deal.id,
      }),
    onSuccess: (_data, deal) => {
      setLocalDeal({
        id: deal.id,
        identifier: deal.identifier,
        name: deal.deal_name,
      })
      invalidateDealQueries()
    },
    onError: () => toast.error('Failed to assign thread to deal'),
  })

  const removeMutation = useMutation({
    mutationFn: () => api.emailIntegration.removeThreadFromDeal(thread.id),
    onSuccess: () => {
      toast.success('Thread removed from deal')
      setLocalDeal({ id: null, identifier: null, name: null })
      invalidateDealQueries()
    },
    onError: () => toast.error('Failed to remove thread from deal'),
  })

  const hideThreadMutation = useMutation({
    mutationFn: () => api.emailIntegration.hideThread(thread.id),
    onSuccess: () => {
      toast.success('Thread hidden')
      setLocalIsHidden(true)
      queryClient.invalidateQueries({ queryKey: ['email-threads'] })
      onBack()
    },
    onError: () => toast.error('Failed to hide thread'),
  })

  const unhideThreadMutation = useMutation({
    mutationFn: () => api.emailIntegration.unhideThread(thread.id),
    onSuccess: () => {
      toast.success('Thread unhidden')
      setLocalIsHidden(false)
      queryClient.invalidateQueries({ queryKey: ['email-threads'] })
    },
    onError: () => toast.error('Failed to unhide thread'),
  })

  const handleRemoveFromDeal = useCallback(() => {
    removeMutation.mutate()
  }, [removeMutation])

  const handleDealAssigned = useCallback(
    (deal: DealSearchResult) => {
      assignMutation.mutate(deal, {
        onSuccess: () => toast.success('Thread assigned to deal'),
      })
    },
    [assignMutation]
  )

  const handleCreateDeal = useCallback(() => {
    createDealOpenedAt.current = new Date().toISOString()
    setShowCreateDeal(true)
  }, [])

  const handleDealCreated = useCallback(() => {
    setShowCreateDeal(false)
    setIsAssigningNewDeal(true)

    // CreateDealModal's bulk endpoint returns empty 200 (no deal ID).
    // Search for deals created after we opened the modal to avoid picking
    // up a pre-existing deal. Backend orders by -created, so the first
    // result is the most recent match.
    const createdAfter = createDealOpenedAt.current ?? undefined
    api.emailIntegration
      .searchDeals({
        contact_company_id: contactCompanyId,
        created_after: createdAfter,
      })
      .then((deals) => {
        if (!deals || deals.length === 0) {
          toast.error(
            'Could not find the created deal — please assign it manually'
          )
          return
        }
        const deal = deals[0]
        assignMutation.mutate(deal, {
          onSuccess: () =>
            toast.success(
              `Thread assigned to Deal #${deal.identifier} (${deal.deal_name})`
            ),
        })
      })
      .catch(() => toast.error('Failed to find the created deal'))
      .finally(() => setIsAssigningNewDeal(false))
  }, [api.emailIntegration, contactCompanyId, assignMutation])

  const handleUpdateSalesStage = useCallback(() => {
    setShowUpdateStage(true)
  }, [])

  const handleScheduleReminder = useCallback(() => {
    setShowReminderModal(true)
  }, [])

  const companyTypeSlug =
    contactCompany?.sub_category?.slug ?? 'restaurants-bars'

  // Submenu content for "Assign to deal" — rendered inside dropdown submenu
  const assignDealSubContent = useMemo(
    () =>
      !localDeal.id ? (
        <DealSearchSubContent
          contactCompanyId={contactCompanyId}
          onAssign={handleDealAssigned}
        />
      ) : undefined,
    [localDeal.id, contactCompanyId, handleDealAssigned]
  )

  // Deal-conditional + always-available action callbacks for action bar & per-message menus
  const dealActionCallbacks = useMemo(
    () => ({
      onAssignToDeal: !localDeal.id ? () => {} : undefined,
      onRemoveFromDeal: localDeal.id ? handleRemoveFromDeal : undefined,
      onCreateDeal: !localDeal.id ? handleCreateDeal : undefined,
      onUpdateSalesStage: localDeal.id ? handleUpdateSalesStage : undefined,
      onScheduleReminder: handleScheduleReminder,
      onHideThread: !localIsHidden
        ? () => hideThreadMutation.mutate()
        : undefined,
      onUnhideThread: localIsHidden
        ? () => unhideThreadMutation.mutate()
        : undefined,
    }),
    [
      localDeal.id,
      handleRemoveFromDeal,
      handleCreateDeal,
      handleUpdateSalesStage,
      handleScheduleReminder,
      localIsHidden,
      hideThreadMutation,
      unhideThreadMutation,
    ]
  )

  // Auto-open compose panel when navigating from inbox row menu action.
  // Uses a ref to consume the mode exactly once per navigation.
  const pendingComposeMode = useRef(initialComposeMode)
  useEffect(() => {
    pendingComposeMode.current = initialComposeMode
  }, [initialComposeMode])

  useEffect(() => {
    if (pendingComposeMode.current && messages?.length) {
      const mode = pendingComposeMode.current
      pendingComposeMode.current = undefined
      setComposeMode(mode)
      setReplyToMessage(messages[messages.length - 1])
    }
  }, [messages])

  // Auto-scroll to bottom when compose panel opens
  useEffect(() => {
    if (composeMode && scrollContainerRef.current) {
      // Small delay to let the compose panel render before scrolling
      requestAnimationFrame(() => {
        scrollContainerRef.current?.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: 'smooth',
        })
      })
    }
  }, [composeMode])

  const handleCompose = useCallback(
    (mode: ComposeMode, message?: EmailMessage) => {
      setComposeMode(mode)
      // If a specific message is provided (per-message action), use it;
      // otherwise default to the last message in the thread
      setReplyToMessage(message || messages?.[messages.length - 1])
    },
    [messages]
  )

  const handleComposeFromBar = useCallback(
    (mode: ComposeMode) => {
      handleCompose(mode, messages?.[messages.length - 1])
    },
    [handleCompose, messages]
  )

  const resetCompose = useCallback(() => {
    setComposeMode(null)
    setReplyToMessage(undefined)
  }, [])

  // Optimistic send: append message to cache immediately, fire API in background
  const sendMutation = useMutation({
    mutationFn: ({ formData, fromId }: SendPayload) =>
      api.emailIntegration.sendEmailMessage(fromId, formData),
    onMutate: async ({ optimisticMessage }: SendPayload) => {
      await queryClient.cancelQueries({
        queryKey: ['thread-messages', thread.id],
      })
      const previous = queryClient.getQueryData<EmailMessage[]>([
        'thread-messages',
        thread.id,
      ])
      queryClient.setQueryData<EmailMessage[]>(
        ['thread-messages', thread.id],
        (old) => [...(old || []), optimisticMessage]
      )
      return { previous }
    },
    onError: async (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          ['thread-messages', thread.id],
          context.previous
        )
      }
      await handleEmailError(_err, api.emailIntegration)
    },
    onSuccess: () => {
      toast.success('Email sent successfully')
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['thread-messages', thread.id],
      })
    },
  })

  const handleSendSuccess = useCallback(
    (payload: SendPayload) => {
      resetCompose()
      sendMutation.mutate(payload)
    },
    [resetCompose, sendMutation]
  )

  const filteredCompanyNames =
    thread.contact_company_names?.filter(Boolean) ?? []
  const companyBadgeNames =
    filteredCompanyNames.length > 0
      ? filteredCompanyNames
      : companyName
        ? [companyName]
        : []

  return (
    <div className="h-full flex flex-col border border-line-subtle rounded-lg overflow-hidden bg-surface">
      {/* Action bar */}
      <div className="px-4 pt-3 pb-3 border-b border-line-subtle">
        <EmailDetailActionBar
          threadIndex={threadIndex}
          totalThreads={totalThreads}
          pageSize={pageSize}
          pageStart={pageStart}
          onBack={onBack}
          onNavigateThread={onNavigateThread}
          onCompose={handleComposeFromBar}
          dealId={localDeal.id}
          assignDealSubContent={assignDealSubContent}
          isHidden={localIsHidden}
          isOwnThread={thread.is_own_thread}
          {...dealActionCallbacks}
        />
      </div>

      {/* Subject + company badge + deal badge */}
      <div className="mt-3 mb-4 pl-[60px] pr-4">
        <h2 className="text-xl font-normal text-fg">{thread.subject}</h2>
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
          {companyBadgeNames.map((name) => (
            <span key={name} className={CATEGORY_BADGE_CLASS}>
              {name}
            </span>
          ))}
          {isAssigningNewDeal && (
            <span className={`${CATEGORY_BADGE_CLASS} animate-pulse`}>
              Assigning deal…
            </span>
          )}
          {localDeal.id && localDeal.identifier && (
            <DealBadge
              dealIdentifier={localDeal.identifier}
              dealName={localDeal.name}
              onClick={() => window.open(`/deals/${localDeal.id}`, '_blank')}
            />
          )}
        </div>
      </div>

      {/* Thread messages (scrollable) */}
      <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto">
        {isLoading ? (
          <ThreadSkeleton />
        ) : (
          (messages || []).map((message: EmailMessage, index: number) => (
            <ThreadMessage
              key={message.id}
              message={message}
              isLast={!composeMode && index === (messages?.length ?? 0) - 1}
              onCompose={handleCompose}
              dealId={localDeal.id}
              assignDealSubContent={assignDealSubContent}
              isHidden={localIsHidden}
              isOwnThread={thread.is_own_thread}
              {...dealActionCallbacks}
            />
          ))
        )}
      </div>

      {/* Inline compose panel — pinned below scroll area so the modal doesn't scroll */}
      {composeMode && thread.is_own_thread && (
        <ComposePanel
          key={`${composeMode}-${replyToMessage?.id}`}
          mode={composeMode}
          thread={thread}
          replyToMessage={replyToMessage}
          integrationEmail={thread.email_integration_email}
          onSend={handleSendSuccess}
          onDiscard={resetCompose}
          onModeChange={setComposeMode}
          dealIdentifier={localDeal.identifier}
          dealName={localDeal.name}
        />
      )}

      {/* Reply bar (hidden when composing or viewing a shared thread) */}
      {!composeMode && thread.is_own_thread && (
        <div className="px-4 pb-4">
          <EmailReplyBar onCompose={handleComposeFromBar} />
        </div>
      )}

      {/* Update Sales Stage modal */}
      {localDeal.id && (
        <UpdateSalesStageModal
          dealId={localDeal.id}
          dealIdentifier={localDeal.identifier}
          open={showUpdateStage}
          onClose={() => setShowUpdateStage(false)}
        />
      )}

      {/* Schedule Reminder modal */}
      {showReminderModal && (
        <Suspense fallback={null}>
          <ReminderModal
            open={showReminderModal}
            onClose={() => setShowReminderModal(false)}
            defaultCompanyId={contactCompanyId}
            defaultDealIds={localDeal.id ? [localDeal.id] : undefined}
            onCreated={() => setShowReminderModal(false)}
          />
        </Suspense>
      )}

      {/* Create Deal modal */}
      {showCreateDeal && contactCompany && (
        <Suspense fallback={null}>
          <CreateDealModal
            show={showCreateDeal}
            handleClose={() => setShowCreateDeal(false)}
            onDealCreated={handleDealCreated}
            targetCompanyId={contactCompanyId}
            totalSelectedRows={1}
            optimisticUiTarget="none"
            companyTypeSlug={companyTypeSlug}
            tableTypeSlug={companyTypeSlug}
          />
        </Suspense>
      )}
    </div>
  )
}

/** Simplified detail view for emailOnlyMode (no thread context). */
interface EmailOnlyDetailViewProps {
  email: EmailMessage
}

export function EmailOnlyDetailView({ email }: EmailOnlyDetailViewProps) {
  const api = useApiService()

  const { data: emailData, isLoading } = useQuery({
    queryKey: ['email-message', email.id],
    queryFn: ({ signal }) =>
      api.emailIntegration.getEmailMessageById(email.id, signal),
  })

  const displayData = emailData || email

  return (
    <div className="h-full flex flex-col border border-line-subtle rounded-lg overflow-hidden bg-surface">
      {/* Single message in scrollable area */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading ? (
          <ThreadSkeleton />
        ) : (
          <ThreadMessage message={displayData} isLast />
        )}
      </div>

      <div className="px-4 pb-4">
        <EmailReplyBar />
      </div>
    </div>
  )
}
